import isEqual from 'lodash/isEqual';
import { ulid } from 'ulid';

import {
  type HeadphoneAction,
  type MobileDocumentSubpanel,
  type Profile,
  type SwipeDocList,
  type SwipeType,
  type UserEvent,
  AppIcon,
  ClientState,
  DisplayThemeSetting,
  DocListSwipeActionEnum,
  MobileDocumentInfoContainerVisiblePanel,
  SubscriptionProduct,
} from '../../../types';
import getServerBaseUrl from '../../../utils/getServerBaseUrl.platform';
// eslint-disable-next-line import/no-cycle
import requestWithAuth from '../../../utils/requestWithAuth.platformIncludingExtension';
// eslint-disable-next-line import/no-cycle
import ttsController from '../../actions/ttsController.platform';
// eslint-disable-next-line import/no-cycle
import { CancelStateUpdate, globalState, isStaffProfile, updateState } from '../../models';
// eslint-disable-next-line import/no-cycle
import { getAutoKindleEmailDelivery } from '../../stateGetters';
// eslint-disable-next-line import/no-cycle
import { createToast } from '../../toasts.platform';
import { StateUpdateOptionsWithoutEventName } from '../../types';
import { setMobileLongFormToolbarId, setMobileShortFormToolbarId } from '../persistentStateUpdaters/toolbars';

export const toggleAutoAdvance = async (
  options: StateUpdateOptionsWithoutEventName & {
    showToast?: boolean;
  },
): Promise<void> => {
  const newValue = !globalState.getState().client.autoAdvance;

  const updateResult = await updateState(
    (state) => {
      state.client.autoAdvance = newValue;
    },
    { ...options, eventName: 'auto-advance-toggled', isUndoable: false },
  );

  if (options.showToast) {
    createToast({
      content: `Auto-advance turned ${newValue ? 'on' : 'off'}`,
      category: 'success',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- updateState always sets userEvent when isUndoable is not false; type is UserEvent at this call site
      undoableUserEventId: (updateResult.userEvent as UserEvent).id,
    });
  }
};

export const toggleIsGhostreaderEnabled = async (
  options: StateUpdateOptionsWithoutEventName & {
    showToast?: boolean;
  },
): Promise<void> => {
  const newValue = !globalState.getState().persistent.settings.isGhostreaderEnabled;
  const correlationId = ulid();

  const stateUpdatePromises: [ReturnType<typeof updateState>, ...Promise<unknown | void>[]] = [
    updateState(
      (state) => {
        state.persistent.settings.isGhostreaderEnabled = newValue;
        // Re-enabling Ghostreader resets this to its default (on)
        if (newValue) {
          delete state.persistent.settings.isFirstClassLookupEnabled;
        }
      },
      {
        correlationId,
        ...options,
        eventName: newValue ? 'ghostreader-enabled' : 'ghostreader-disabled',
      },
    ),
  ];

  // Mobile toolbar setting side-effects
  if (isStaffProfile()) {
    stateUpdatePromises.push(
      setMobileLongFormToolbarId(newValue ? 'modern' : 'classic', {
        correlationId,
        ...options,
      }),
      setMobileShortFormToolbarId(newValue ? 'modern' : 'classic', {
        correlationId,
        ...options,
      }),
    );
  }

  const [updateResult] = await Promise.all(stateUpdatePromises);

  if (options.showToast) {
    createToast({
      content: `Ghostreader (AI) ${newValue ? 'enabled' : 'disabled'}`,
      category: 'success',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- updateState always sets userEvent when isUndoable is not false; type is UserEvent at this call site
      undoableUserEventId: (updateResult.userEvent as UserEvent).id,
    });
  }
};

export const GHOSTREADER_CUSTOM_INSTRUCTIONS_MAX_LENGTH = 2000;

export const setGhostreaderCustomInstructions = async (
  value: string,
  options: StateUpdateOptionsWithoutEventName & {
    showToast?: boolean;
  },
): Promise<void> => {
  const normalized = value.slice(0, GHOSTREADER_CUSTOM_INSTRUCTIONS_MAX_LENGTH).trim();

  await updateState(
    (state) => {
      if (normalized) {
        state.persistent.settings.ghostreaderCustomInstructions = normalized;
      } else {
        delete state.persistent.settings.ghostreaderCustomInstructions;
      }
    },
    { ...options, eventName: 'ghostreader-custom-instructions-updated', isUndoable: false },
  );

  if (options.showToast) {
    createToast({
      content: normalized ? 'Custom instructions saved' : 'Custom instructions cleared',
      category: 'success',
    });
  }
};

export const toggleIsQuickLookupEnabled = async (
  options: StateUpdateOptionsWithoutEventName & {
    showToast?: boolean;
  },
): Promise<void> => {
  // `isFirstClassLookupEnabled` is Quick Lookup (we renamed it)
  const newValue = globalState.getState().persistent.settings.isFirstClassLookupEnabled === false;

  const updateResult = await updateState(
    (state) => {
      state.persistent.settings.isFirstClassLookupEnabled = newValue;
    },
    {
      ...options,
      eventName: newValue ? 'quick-lookup-enabled' : 'quick-lookup-disabled',
    },
  );

  if (options.showToast) {
    createToast({
      content: `Quick lookup ${newValue ? 'enabled' : 'disabled'}`,
      category: 'success',
      undoableUserEventId: updateResult.userEvent!.id,
    });
  }
};

export const toggleShouldOpenReaderLinksInDesktopApp = async (
  options: StateUpdateOptionsWithoutEventName & {
    shouldShowToast?: boolean;
  },
): Promise<void> => {
  const newValue = !globalState.getState().client.shouldOpenReaderLinksInDesktopApp;

  const updateResult = await updateState(
    (state) => {
      state.client.shouldOpenReaderLinksInDesktopApp = newValue;
    },
    { ...options, eventName: 'open-reader-links-on-desktop-toggled', isUndoable: false },
  );

  if (options.shouldShowToast) {
    createToast({
      content: `Open Reader links in desktop app turned ${newValue ? 'on' : 'off'}`,
      category: 'success',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- updateState always sets userEvent when isUndoable is not false; type is UserEvent at this call site
      undoableUserEventId: (updateResult.userEvent as UserEvent).id,
    });
  }
};

const setAutoKindleEmailSend = async ({
  isEnabled,
  shouldShowToast,
  userInteraction,
}: {
  isEnabled: boolean;
  shouldShowToast?: boolean;
  userInteraction: string;
}): Promise<void> => {
  await updateState(
    (state) => {
      state.persistent.integrations.kindle ??= {};
      state.persistent.integrations.kindle.isAutomaticKindleEmailEnabled = isEnabled;
    },
    {
      eventName: 'auto-kindle-email-send-setting-updated',
      userInteraction,
    },
  );

  if (shouldShowToast) {
    createToast({
      category: 'success',
      content: `Automatic Delivery ${isEnabled ? 'enabled' : 'disabled'}`,
    });
  }
};

export const toggleAutoKindleEmailSend = (userInteraction: string) => {
  setAutoKindleEmailSend({
    isEnabled: !getAutoKindleEmailDelivery(globalState.getState()),
    shouldShowToast: true,
    userInteraction,
  });
};

export const setKindleExportFrequency = async ({
  exportFrequency,
  userInteraction,
  shouldShowToast = true,
}: {
  exportFrequency: 'weekly' | 'daily';
  userInteraction: string;
  shouldShowToast?: boolean;
}) => {
  await updateState(
    (state) => {
      state.persistent.integrations.kindle ??= {};
      state.persistent.integrations.kindle.exportFrequency = exportFrequency;
    },
    { eventName: 'kindle-export-frequency-updated', userInteraction },
  );

  if (shouldShowToast) {
    createToast({
      category: 'success',
      content: `Frequency set to '${exportFrequency}'`,
    });
  }
};

export const optimisticallyGrantUserFullSubscription = async (stateUpdateOptions: StateUpdateOptionsWithoutEventName) => {
  await updateState(
    (state) => {
      if (!state.client.profile) {
        return;
      }
      state.client.profile.subscription.product = SubscriptionProduct.Full;
    },
    { ...stateUpdateOptions, eventName: 'optimistically-grant-user-full-subscription', isUndoable: false },
  );
};

export const toggleReadwiseEmailPreferences = async ({
  stateUpdateOptions,
  emailPreferenceKey,
  prettyNameForToast,
}: {
  stateUpdateOptions: StateUpdateOptionsWithoutEventName;
  emailPreferenceKey: keyof Profile['email_preferences'];
  prettyNameForToast: string;
}) => {
  const newValue = !globalState.getState().client.profile?.email_preferences[emailPreferenceKey];

  await updateState(
    (state) => {
      if (!state.client.profile) {
        return;
      }
      state.client.profile.email_preferences[emailPreferenceKey] = newValue;
    },
    { ...stateUpdateOptions, eventName: 'email-subscription-toggled', isUndoable: false },
  );

  await requestWithAuth(
    `${getServerBaseUrl()}/reader/api/email_preferences_toggle/${emailPreferenceKey}/${newValue}`,
    {
      method: 'POST',
      credentials: 'include',
      mode: 'cors',
    },
  );

  createToast({
    content: `${prettyNameForToast} turned ${newValue ? 'off' : 'on'}`,
    category: 'success',
  });
};

export const toggleMobileLargeFeedView = async (
  options: StateUpdateOptionsWithoutEventName,
): Promise<void> => {
  await updateState(
    (state) => {
      state.client.largeFeedView = !state.client.largeFeedView;
    },
    { ...options, eventName: 'mobile-toggle-large-feed-view', isUndoable: false },
  );
};

export const toggleAskToPasteUrls = async (): Promise<void> => {
  await updateState(
    (state) => {
      state.client.askToPasteUrls = !state.client.askToPasteUrls;
    },
    { userInteraction: 'unknown', eventName: 'mobile-toggle-ask-to-paste-url', isUndoable: false },
  );
};

export const toggleKeepAwakeWhileReading = async (): Promise<void> => {
  await updateState(
    (state) => {
      state.client.keepAwakeWhileReading = !state.client.keepAwakeWhileReading;
    },
    { userInteraction: 'unknown', eventName: 'mobile-toggle-keep-awake' },
  );
};

export const toggleHapticFeedback = async (): Promise<void> => {
  await updateState(
    (state) => {
      state.client.mobileHapticsEnabled = !state.client.mobileHapticsEnabled;
    },
    { userInteraction: 'unknown', eventName: 'mobile-toggle-haptics' },
  );
};

export const toggleShakeToReport = async (): Promise<void> => {
  await updateState(
    (state) => {
      state.client.shakeToReportEnabled = !state.client.shakeToReportEnabled;
    },
    { userInteraction: 'unknown', eventName: 'mobile-toggle-shake-to-report' },
  );
};

export const toggleOpenLinksInApp = async (): Promise<void> => {
  await updateState(
    (state) => {
      state.client.openLinksInApp = !state.client.openLinksInApp;
    },
    { userInteraction: 'unknown', eventName: 'mobile-toggle-open-links-in-app' },
  );
};

export const updateMobileAppIcon = async (appIcon: AppIcon): Promise<void> => {
  await updateState(
    (state) => {
      state.client.mobileAppIcon = appIcon;
    },
    { userInteraction: 'unknown', eventName: 'mobile-change-app-icon' },
  );
};

export const updateHeadphoneGesture = async ({
  name,
  action,
}: {
  name: 'nextTrack' | 'previousTrack';
  action: HeadphoneAction;
}): Promise<void> => {
  await updateState(
    (state) => {
      state.client.headphoneGestures[name] = action;
    },
    { userInteraction: 'unknown', eventName: 'mobile-headphone-gesture-updated' },
  );
};

export const setTheme = async (
  displayTheme: DisplayThemeSetting,
  options: StateUpdateOptionsWithoutEventName,
): Promise<void> => {
  await updateState(
    (state) => {
      state.client.theme = displayTheme;
    },
    { ...options, eventName: 'theme-updated', isUndoable: false },
  );
};

export const setHasBeenShownReview = async (
  val: boolean,
  options: StateUpdateOptionsWithoutEventName,
): Promise<void> => {
  await updateState(
    (state) => {
      state.persistent.hasBeenShownReview = val;
    },
    { ...options, eventName: 'shown-review-prompt-updated' },
  );
};

export const toggleDarkModeTheme = async (): Promise<void> => {
  await updateState(
    (state) => {
      state.client.theme =
        state.client.theme === DisplayThemeSetting.Default ? DisplayThemeSetting.Dark : DisplayThemeSetting.Default;
    },
    { eventName: 'dark-mode-theme-updated', userInteraction: 'unknown', isUndoable: false },
  );
};

export const toggleHidePanelsOnEnteringReadingView = async (
  options: StateUpdateOptionsWithoutEventName,
): Promise<void> => {
  const newValue = !globalState.getState().client.hideLeftPanelOnEnteringReadingView;

  const updateResult = await updateState(
    (state) => {
      state.client.hideLeftPanelOnEnteringReadingView = newValue;
      state.client.hideRightPanelOnEnteringReadingView = newValue;
    },
    { ...options, isUndoable: false, eventName: 'reading-view-panels-toggled-hidden-by-default' },
  );

  createToast({
    content: `Hide panels on entering reading view ${newValue ? 'on' : 'off'}`,
    category: 'success',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- updateState always sets userEvent when isUndoable is not false; type is UserEvent at this call site
    undoableUserEventId: (updateResult.userEvent as UserEvent).id,
  });
};

export const toggleHideRightPanelOnEnteringReadingView = async (
  options: StateUpdateOptionsWithoutEventName,
): Promise<void> => {
  const newValue = !globalState.getState().client.hideRightPanelOnEnteringReadingView;

  const updateResult = await updateState(
    (state) => {
      state.client.hideRightPanelOnEnteringReadingView = newValue;
      state.client.hideLeftPanelOnEnteringReadingView = false;
    },
    { ...options, isUndoable: false, eventName: 'reading-view-right-panel-toggled-hidden-by-default' },
  );

  createToast({
    content: `Hide right panel on entering reading view ${newValue ? 'on' : 'off'}`,
    category: 'success',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- updateState always sets userEvent when isUndoable is not false; type is UserEvent at this call site
    undoableUserEventId: (updateResult.userEvent as UserEvent).id,
  });
};

export const toggleHideLeftPanelOnEnteringReadingView = async (
  options: StateUpdateOptionsWithoutEventName,
): Promise<void> => {
  const newValue = !globalState.getState().client.hideLeftPanelOnEnteringReadingView;

  const updateResult = await updateState(
    (state) => {
      state.client.hideLeftPanelOnEnteringReadingView = newValue;
      state.client.hideRightPanelOnEnteringReadingView = false;
    },
    { ...options, isUndoable: false, eventName: 'reading-view-left-panel-toggled-hidden-by-default' },
  );

  createToast({
    content: `Hide left panel on entering reading view ${newValue ? 'on' : 'off'}`,
    category: 'success',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- updateState always sets userEvent when isUndoable is not false; type is UserEvent at this call site
    undoableUserEventId: (updateResult.userEvent as UserEvent).id,
  });
};

export const setProfile = (profile: Profile, options: StateUpdateOptionsWithoutEventName): void => {
  updateState(
    (state) => {
      if (isEqual(state.client.profile, profile)) {
        throw new CancelStateUpdate();
      }
      state.client.profile = profile;
    },
    {
      ...options,
      eventName: 'profile-updated',
    },
  );
};

export const updateSwipeAction = async (
  docList: SwipeDocList,
  swipeType: string,
  swipeAction: string,
  options: StateUpdateOptionsWithoutEventName,
): Promise<void> => {
  updateState(
    (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- swipeType and swipeAction are string params validated at the call site to be SwipeType/DocListSwipeActionEnum values
      if (state.client.swipeConfig?.[docList]?.[swipeType as SwipeType]) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- same as above — runtime values are SwipeType/DocListSwipeActionEnum; string typing is for API flexibility
        state.client.swipeConfig[docList][swipeType as SwipeType] = swipeAction as DocListSwipeActionEnum;
      }
    },
    {
      ...options,
      eventName: 'swipe-action-updated',
      shouldCreateUserEvent: false, // We don't care when this happens
      isUndoable: false,
    },
  );
};

export const setMobileLastAnnotationSubpanelOpened = (
  mobileLastAnnotationSubpanelOpened: MobileDocumentSubpanel,
) => {
  updateState(
    (state) => {
      state.client.mobileLastAnnotationSubpanelOpened = mobileLastAnnotationSubpanelOpened;
    },
    {
      eventName: 'set-mobile-last-annotation-subpanel-opened',
      shouldCreateUserEvent: false,
      userInteraction: 'tap',
      isUndoable: false,
    },
  );
};

export const setMobileSafeAreaInsets = async (safeAreaInsets: ClientState['mobileSafeAreaInsets']) => {
  const currentState = globalState.getState();
  if (isEqual(currentState.client.mobileSafeAreaInsets, safeAreaInsets)) {
    return;
  }
  return updateState(
    (state) => {
      state.client.mobileSafeAreaInsets = safeAreaInsets;
    },
    {
      eventName: 'set-mobile-safe-area-insets-updated',
      shouldCreateUserEvent: false,
      userInteraction: null,
      isUndoable: false,
    },
  );
};

export const setMobileDimensions = async (dimensions: ClientState['mobileDimensions']) => {
  const currentState = globalState.getState();
  if (isEqual(currentState.client.mobileDimensions, dimensions)) {
    return;
  }

  return updateState(
    (state) => {
      state.client.mobileDimensions = dimensions;
    },
    {
      eventName: 'set-mobile-dimensions-updated',
      shouldCreateUserEvent: false,
      userInteraction: null,
      isUndoable: false,
    },
  );
};

export const toggleMobileDeveloperSetting = async (
  setting: keyof ClientState['mobileDeveloperSettings'],
  options: StateUpdateOptionsWithoutEventName,
) => {
  const newValue = !globalState.getState().client.mobileDeveloperSettings[setting];

  return updateState(
    (state) => {
      state.client.mobileDeveloperSettings[setting] = newValue;
    },
    { ...options, eventName: 'developer-options-toggled', isUndoable: false },
  );
};

export const toggleFeatureFlagSetting = async (
  setting: keyof ClientState['featureFlags'],
  options: StateUpdateOptionsWithoutEventName,
) => {
  const newValue = !globalState.getState().client.featureFlags[setting];

  return updateState(
    (state) => {
      state.client.featureFlags[setting] = newValue;
    },
    { ...options, eventName: 'feature-flag-toggled', isUndoable: false },
  );
};

export const setIsLibraryCollapsed = async (
  isLibraryCollapsed: boolean,
  options?: Omit<Parameters<typeof updateState>[1], 'eventName'>,
): Promise<void> => {
  await updateState(
    (state) => {
      state.client.navigationSidebar.isLibraryCollapsed = isLibraryCollapsed;
    },
    {
      ...options ?? {},
      userInteraction: options?.userInteraction ?? 'click',
      isUndoable: false,
      eventName: 'is-library-collapsed-changed',
    },
  );
};

export const setIsTagsCollapsed = async (
  isTagsCollapsed: boolean,
  options?: Omit<Parameters<typeof updateState>[1], 'eventName'>,
): Promise<void> => {
  await updateState(
    (state) => {
      state.client.navigationSidebar.isTagsCollapsed = isTagsCollapsed;
    },
    {
      ...options ?? {},
      userInteraction: options?.userInteraction ?? 'click',
      isUndoable: false,
      eventName: 'is-tags-collapsed-changed',
    },
  );
};

export const setActiveSchemaMigration = async (value: boolean) => {
  await updateState(
    (state) => {
      state.activeSchemaMigration = value;
    },
    { eventName: 'active-schema-migration-updated', userInteraction: null },
  );
};

export const setHasUserAuthedIntoBookstore = async (value: boolean) => {
  await updateState(
    (state) => {
      if (state.client.hasUserAuthedIntoBookstore === value) {
        throw new CancelStateUpdate();
      }
      state.client.hasUserAuthedIntoBookstore = value;
    },
    { eventName: 'user-authed-into-bookstore', userInteraction: null },
  );
};

export const saveDocumentCoverDimensions = async (imageUrl: string, width: number, height: number) => {
  await updateState(
    (state) => {
      state.client.documentCoverDimensionsMap[imageUrl] = { width, height };
    },
    { eventName: 'save-document-cover-image-dimensions', userInteraction: null },
  );
};

export const saveImageDominantColors = async (imageUrl: string, colors: string[]) => {
  await updateState(
    (state) => {
      state.client.imageColorCache[imageUrl] = colors;
    },
    { eventName: 'save-image-dominant-colors', userInteraction: null },
  );
};

export const setMobileIsDrawerOpen = async (value: boolean) => {
  await updateState(
    (state) => {
      state.client.mobileIsDrawerOpen = value;
    },
    { eventName: 'set-mobile-is-drawer-open', userInteraction: null },
  );
};

export const setMobileIsReadingViewLeftSidebarOpen = async (value: boolean) => {
  await updateState(
    (state) => {
      if (state.client.mobileIsReadingViewLeftSidebarOpen === value) {
        throw new CancelStateUpdate();
      }
      state.client.mobileIsReadingViewLeftSidebarOpen = value;

      if (value) {
        state.client.mobileIsReadingViewRightSidebarOpen = false;
      }
    },
    { eventName: 'set-mobile-is-reading-view-left-sidebar-open', userInteraction: null },
  );
};

export const setMobileIsReadingViewRightSidebarOpen = async (value: boolean) => {
  await updateState(
    (state) => {
      if (state.client.mobileIsReadingViewRightSidebarOpen === value) {
        throw new CancelStateUpdate();
      }
      state.client.mobileIsReadingViewRightSidebarOpen = value;

      if (value) {
        state.client.mobileIsReadingViewLeftSidebarOpen = false;
      }
    },
    { eventName: 'set-mobile-is-reading-view-right-sidebar-open', userInteraction: null },
  );
};

export const setMobileDocumentInfoVisiblePanel = async (value: MobileDocumentInfoContainerVisiblePanel) => {
  await updateState(
    (state) => {
      state.client.mobileDocumentInfoVisiblePanel = value;
    },
    { eventName: 'set-mobile-document-info-visible-panel', userInteraction: null },
  );
};

export const toggleAssistedReadingSetting = async (): Promise<void> => {
  const newValue = !globalState.getState().persistent.bookwise.settings.isAssistedReadingEnabled;

  await updateState(
    (state) => {
      state.persistent.bookwise.settings.isAssistedReadingEnabled = newValue;
    },
    { userInteraction: null, eventName: 'assisted-reading-enabled-toggled' },
  );
  if (!newValue) {
    await ttsController.stop();
  }
};
